import {
  Autocomplete,
  CircularProgress,
  TextField,
  TextFieldProps,
} from "@mui/material";
import { AutocompleteWithQueryProps } from "components/AutocompleteWithQuery";
import { ALL_APPROVED_ID } from "helpers/skuQualificationHelpers";
import { ANY_PROFESSION_ID } from "constants/skuQualificationConstants";
import useGlobalContext from "hooks/globalData";
import { useEffect } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery } from "react-query";
import {
  ListRequestOfBaseFilter,
  Profession,
} from "services/KnowfullyAdminClient";

export { ANY_PROFESSION_ID };

type SelectProfessionExtendedProps = {
  required?: boolean;
  textFieldProps: Partial<TextFieldProps>;
  anyProfessionAllowed?: boolean;
} & Omit<
  AutocompleteWithQueryProps<Profession>,
  "fetchedData" | "failMessage" | "labelField"
>;

const SelectProfessionExtended = ({
  value,
  required,
  textFieldProps,
  anyProfessionAllowed = true,
  onChange,
  ...rest
}: SelectProfessionExtendedProps) => {
  const { formatMessage: t } = useIntl();
  const { skuService } = useGlobalContext();

  const { isLoading, isError, data } = useQuery(["professions"], () =>
    skuService.getProfessions(
      ListRequestOfBaseFilter.fromJS({
        sortField: "name",
      })
    )
  );

  const additionalProfessions = anyProfessionAllowed
    ? [
        {
          id: ALL_APPROVED_ID,
          name: t({ id: "allApprovedProfessionsLabel" }),
        } as Profession,
        {
          id: ANY_PROFESSION_ID,
          name: t({ id: "anyLabel" }),
        } as Profession,
      ]
    : [
        {
          id: ALL_APPROVED_ID,
          name: t({ id: "allApprovedProfessionsLabel" }),
        } as Profession,
      ];

  const professions = additionalProfessions.concat(data?.data ?? []);

  useEffect(() => {
    if (!isLoading) {
      if (value && !professions.find((d) => d.id === value)) {
        onChange(undefined);
      }
    }
  }, [isLoading, professions, value, onChange]);

  if (isError) {
    return (
      <span>
        <T id="fetchError" />
      </span>
    );
  }

  const selectedValue = professions.find((d) => d.id === value) ?? null;

  const renderOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: Profession
  ) => {
    if (option.id === ANY_PROFESSION_ID || option.id === ALL_APPROVED_ID) {
      return (
        <li {...props}>
          <b>{option.name}</b>
        </li>
      );
    }

    return <li {...props}>{option.name}</li>;
  };

  return (
    <Autocomplete
      autoComplete
      loading={isLoading}
      disabled={isLoading}
      options={professions}
      value={selectedValue}
      renderOption={renderOption}
      getOptionLabel={(option) => option.name!}
      onChange={(e, v) => onChange && onChange(v?.id)}
      {...rest}
      renderInput={(input) => (
        <TextField
          required
          variant="standard"
          {...textFieldProps}
          {...input}
          InputProps={{
            ...textFieldProps.InputProps,
            ...input.InputProps,
            endAdornment: (
              <>
                {isLoading ? <CircularProgress size={16} /> : null}
                {input.InputProps.endAdornment}
              </>
            ),
          }}
          label={t({ id: "professionLabel" })}
          fullWidth
        />
      )}
    />
  );
};

export default SelectProfessionExtended;
